import MDButton from "components/MDButton";
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";
import { useDispatch, useSelector } from "react-redux";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SkLoading from "components/SkLoading";
import MDInput from "components/MDInput";
import AstrieskIcon from "components/AstrieskIcon";
import { Card, MenuItem } from "@mui/material";
import { handleAlert } from "redux/festures/alertSlice";
import { createTax, updateTax, getAllTax } from "redux/festures/taxSlice";

const APPLY_ON_OPTIONS = ["SERVICE", "ECOMMERCE"];

const Form = ({ isOpenUpdate, setIsOpenUpdate, setIsOpen }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [taxData, setTaxData] = useState({
    taxPercent: "",
    applyOn: "",
  });
  const admin = localStorage.getItem("admin_id");
  const dispatch = useDispatch();
  const { Loading, tax, singleTax } = useSelector((state) => ({ ...state?.isTax }));

  const existingTypes = useMemo(() => {
    if (!Array.isArray(tax)) return [];
    return tax.map((t) => t?.applyOn).filter(Boolean);
  }, [tax]);

  const availableApplyOn = useMemo(() => {
    if (isOpenUpdate) return APPLY_ON_OPTIONS;
    return APPLY_ON_OPTIONS.filter((type) => !existingTypes.includes(type));
  }, [existingTypes, isOpenUpdate]);

  useEffect(() => {
    if (singleTax && isOpenUpdate) {
      setTaxData({
        taxPercent: singleTax?.taxPercent ?? "",
        applyOn: singleTax?.applyOn || "",
      });
    } else {
      setTaxData({
        taxPercent: "",
        applyOn: availableApplyOn[0] || "",
      });
    }
  }, [singleTax, isOpenUpdate, availableApplyOn]);

  const handleForm = (e) => {
    const { name, value } = e.target;
    setTaxData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!taxData.taxPercent && taxData.taxPercent !== 0) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "warning",
          msg: "Tax percent is required",
        })
      );
      return;
    }

    if (isOpenUpdate) {
      dispatch(
        updateTax({
          url: `${process.env.REACT_APP_APII}/updateTax/${singleTax?._id}/${admin}`,
          data: { taxPercent: taxData.taxPercent },
        })
      ).then((data) => {
        dispatch(
          handleAlert({
            isOpen: true,
            type: `${data?.payload?.success ? "success" : "error"}`,
            msg: data?.payload?.message,
          })
        );
        if (data?.payload?.success) {
          setIsOpen(false);
          setIsOpenUpdate(false);
          dispatch(getAllTax(`${process.env.REACT_APP_APII}/getAllTax/${admin}`));
        }
      });
      return;
    }

    if (!taxData.applyOn) {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "warning",
          msg: "Both SERVICE and ECOMMERCE taxes already exist. Please update an existing tax.",
        })
      );
      return;
    }

    dispatch(
      createTax({
        url: `${process.env.REACT_APP_APII}/createTax/${admin}`,
        data: {
          taxPercent: taxData.taxPercent,
          applyOn: taxData.applyOn,
        },
      })
    ).then((data) => {
      dispatch(
        handleAlert({
          isOpen: true,
          type: `${data?.payload?.success ? "success" : "error"}`,
          msg: data?.payload?.message,
        })
      );
      if (data?.payload?.success) {
        setIsOpen(false);
        setIsOpenUpdate(false);
        setTaxData({ taxPercent: "", applyOn: "" });
        dispatch(getAllTax(`${process.env.REACT_APP_APII}/getAllTax/${admin}`));
      }
    });
  };

  return Loading ? (
    <SkLoading />
  ) : (
    <>
      <MDBox
        display="flex"
        alignItems="center"
        lineHeight={1}
        sx={{
          justifyContent: "center",
          textAlign: "center",
          flexDirection: "column",
          gap: 5,
          width: "100%",
        }}
      >
        <Card
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 , 10px",
          }}
        >
          <MDTypography variant="h6" py={0.9}>
            {isOpenUpdate ? "Update Tax" : "Create Tax"}
          </MDTypography>
        </Card>
        <MDBox
          display="flex"
          alignItems="center"
          lineHeight={1}
          sx={({ palette: { dark, white } }) => ({
            border: 0.5,
            borderColor: darkMode ? white.main : dark.main,
            borderRadius: 3,
            p: 3,
            justifyContent: "center",
            textAlign: "center",
            flexDirection: "column",
            gap: 3,
            width: "100%",
          })}
          component="form"
          role="form"
          onSubmit={handleFormSubmit}
        >
          <MDBox
            lineHeight={1}
            gap={3}
            width={"90%"}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <MDTypography variant="h6">
              Apply On <AstrieskIcon />
            </MDTypography>
            <MDInput
              required={!isOpenUpdate}
              select
              fullWidth
              name="applyOn"
              value={taxData?.applyOn || ""}
              onChange={handleForm}
              disabled={isOpenUpdate || availableApplyOn.length === 0}
              SelectProps={{ native: false }}
            >
              {isOpenUpdate ? (
                <MenuItem value={taxData.applyOn}>{taxData.applyOn}</MenuItem>
              ) : availableApplyOn.length ? (
                availableApplyOn.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  Both types already created
                </MenuItem>
              )}
            </MDInput>
          </MDBox>

          <MDBox
            lineHeight={1}
            gap={3}
            width={"90%"}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <MDTypography variant="h6">
              Tax Percent <AstrieskIcon />
            </MDTypography>
            <MDInput
              required={true}
              type="number"
              placeholder="Tax Percent"
              fullWidth
              name="taxPercent"
              value={taxData?.taxPercent}
              inputProps={{ min: 0, step: "0.01" }}
              onChange={handleForm}
            />
          </MDBox>

          <MDBox
            sx={{
              width: "100%",
              justifyContent: "flex-end",
              textAlign: "center",
              display: "flex",
            }}
          >
            <MDButton color={"info"} verdant={"gradient"} type={"submit"}>
              {isOpenUpdate ? "Update Tax" : "Create Tax"}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </>
  );
};

export default Form;

Form.propTypes = {
  isOpenUpdate: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.any,
  setIsOpenUpdate: PropTypes.any,
};
